import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as admin from '../../api/admin.ts';
import { formatRelative } from '../../lib/format.ts';
import { toast } from '../../store/toast.ts';

const JOB_LABEL: Record<string, { title: string; description: string }> = {
  purge: { title: '휴지통 파기', description: '보관 기간이 지난 휴지통 항목을 영구 삭제합니다' },
  'history-prune': { title: '버전 정리', description: '보존 정책에 따라 오래된 파일 버전을 제거합니다' },
  gc: { title: '블록 회수', description: '참조가 사라진 블록을 디스크에서 지웁니다 (24시간 유예)' },
  'mirror-repair': { title: '미러 복구', description: '실제 파일 트리와 DB 가 어긋난 항목을 다시 씁니다' },
  'orphan-scan': { title: '고아 블록 정리', description: 'DB 에 없는 디스크 블록을 찾아 제거합니다' },
  'usage-recalc': { title: '사용량 보정', description: '사용자 용량 집계를 실제 값으로 맞춥니다' },
  cleanup: { title: '만료 데이터 정리', description: '만료 세션·페어링 코드·오래된 감사 로그를 지웁니다' },
};

export function JobsTab() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin-jobs'], queryFn: admin.jobs, refetchInterval: 5000,
  });

  const run = useMutation({
    mutationFn: (v: { name: string; grace?: number }) => admin.runJob(v.name, v.grace),
    onSuccess: (r) => {
      toast.success(`${r.name} 실행 완료`);
      void queryClient.invalidateQueries({ queryKey: ['admin-jobs'] });
      void queryClient.invalidateQueries({ queryKey: ['admin-stats'] });
    },
    onError: (e: Error) => { toast.error(e); },
  });

  if (isLoading) return <span className="spinner" />;

  return (
    <>
      <p className="panel__hint" style={{ marginBottom: 12 }}>
        모든 잡은 정해진 시각에 자동 실행됩니다. 아래 버튼은 즉시 한 번 더 돌릴 때만 쓰세요.
      </p>

      {(data?.jobs ?? []).map((job) => {
        const meta = JOB_LABEL[job.name];
        return (
          <div key={job.name} className="frow" style={{ gridTemplateColumns: '220px 110px 1fr 190px' }}>
            <div className="frow__name">
              <div style={{ minWidth: 0 }}>
                <div className="row" style={{ gap: 6 }}>
                  <strong style={{ fontSize: 13 }}>{meta?.title ?? job.name}</strong>
                  {job.running && <span className="spinner" />}
                </div>
                <div className="faint" style={{ fontSize: 11 }}>{meta?.description ?? job.name}</div>
              </div>
            </div>
            <div className="frow__meta mono" title="cron 표현식">{job.schedule}</div>
            <div className="frow__meta truncate">
              {job.last_run_at ? (
                <>
                  <span style={{ color: job.ok ? 'var(--c-success)' : 'var(--c-danger)' }}>
                    {job.ok ? '성공' : '실패'}
                  </span>
                  {' · '}{formatRelative(job.last_run_at)}
                  <div className="faint mono truncate" style={{ fontSize: 11 }}>
                    {typeof job.detail === 'object' && job.detail !== null
                      ? Object.entries(job.detail as Record<string, unknown>)
                        .map(([k, v]) => `${k}=${String(v)}`).join(' ')
                      : String(job.detail ?? '')}
                  </div>
                </>
              ) : '실행 기록 없음'}
            </div>
            <div className="row">
              <button
                className="btn btn--sm" disabled={job.running || run.isPending}
                onClick={() => { run.mutate({ name: job.name }); }}
              >
                지금 실행
              </button>
              {job.name === 'gc' && (
                <button
                  className="btn btn--sm" disabled={job.running || run.isPending}
                  title="24시간 유예를 무시하고 즉시 회수합니다"
                  onClick={() => { run.mutate({ name: 'gc', grace: 0 }); }}
                >
                  즉시 회수
                </button>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
}
